import { useState } from 'react';
import {
  JURISDICTIONS,
  STATUS_LABELS,
  MAP_GRID_COLS,
  MAP_GRID_ROWS,
  countByStatus,
  type Jurisdiction,
  type CoverageStatus,
} from '@/data/coverage';
import { cn } from '@/lib/cn';

/**
 * ArgentinaMap — schematic federal grid (5 cols × 10 rows, north→south, west→east).
 *
 * The grid is intentionally not a topographic SVG: the negative space draws an
 * approximation of Argentina's silhouette while keeping every jurisdiction equally
 * legible. Each jurisdiction is a focusable cell whose status is encoded as a fill
 * color. A sticky side panel reflects the currently hovered/focused cell and shows
 * the legend with counts.
 *
 * a11y: cells are real <button>s with aria-label "Nombre — Estado". Hover and focus
 * are kept in sync; the panel is informational (role="status", aria-live="polite"),
 * not a dialog — there is nothing to dismiss.
 *
 * `compact` mode (used in Home): smaller cells, no side panel, legend below.
 */
export function ArgentinaMap({ compact = false }: { compact?: boolean } = {}) {
  const [active, setActive] = useState<Jurisdiction | null>(null);
  const counts = countByStatus();

  return (
    <div
      className={cn(
        'grid items-start gap-8',
        !compact && 'lg:grid-cols-[1fr_280px] lg:gap-10',
      )}
    >
      <MapGrid
        active={active}
        onEnter={setActive}
        onLeave={() => setActive(null)}
        compact={compact}
      />

      {compact ? (
        <CompactLegend counts={counts} />
      ) : (
        <aside className="space-y-6 lg:sticky lg:top-24">
          <DetailPanel active={active} />
          <Legend counts={counts} />
        </aside>
      )}
    </div>
  );
}

// ─── Grid ────────────────────────────────────────────────────────────────────

function MapGrid({
  active,
  onEnter,
  onLeave,
  compact,
}: {
  active: Jurisdiction | null;
  onEnter: (j: Jurisdiction) => void;
  onLeave: () => void;
  compact: boolean;
}) {
  return (
    <div
      role="group"
      aria-label="Mapa de cobertura por jurisdicción"
      className="relative grid gap-1.5"
      style={{
        gridTemplateColumns: `repeat(${MAP_GRID_COLS}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${MAP_GRID_ROWS}, auto)`,
      }}
    >
      {JURISDICTIONS.map((j) => (
        <JurisdictionCell
          key={j.id}
          jurisdiction={j}
          isActive={active?.id === j.id}
          onEnter={() => onEnter(j)}
          onLeave={onLeave}
          compact={compact}
        />
      ))}
    </div>
  );
}

const STATUS_FILL: Record<CoverageStatus, string> = {
  covered:
    'bg-accent text-accent-contrast border-accent hover:bg-accent/90',
  progress:
    'bg-status-progress/25 text-ink border-status-progress/50 hover:bg-status-progress/35',
  pending:
    'bg-paper-tinted text-ink-muted border-line hover:bg-paper-tinted/80 hover:border-line_strong',
};

function JurisdictionCell({
  jurisdiction,
  isActive,
  onEnter,
  onLeave,
  compact,
}: {
  jurisdiction: Jurisdiction;
  isActive: boolean;
  onEnter: () => void;
  onLeave: () => void;
  compact: boolean;
}) {
  const { row, col } = jurisdiction.gridPosition;
  return (
    <button
      type="button"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      className={cn(
        'focus-ring flex items-center justify-center border font-mono uppercase tracking-wide transition-colors',
        compact ? 'h-9 text-[0.65rem] md:h-10' : 'h-12 text-xs md:h-14 md:text-[0.8rem]',
        STATUS_FILL[jurisdiction.status],
        isActive && 'ring-2 ring-offset-2 ring-offset-paper ring-accent',
      )}
      style={{ gridColumn: col + 1, gridRow: row + 1 }}
      aria-label={`${jurisdiction.name} — ${STATUS_LABELS[jurisdiction.status].label}`}
    >
      {jurisdiction.code}
    </button>
  );
}

// ─── Side panel ──────────────────────────────────────────────────────────────

function DetailPanel({ active }: { active: Jurisdiction | null }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-[180px] rounded-lg border border-line bg-paper p-5"
    >
      {active ? <DetailContent jurisdiction={active} /> : <DetailIdle />}
    </div>
  );
}

function DetailIdle() {
  return (
    <p className="text-sm leading-relaxed text-ink-muted">
      Pasá el cursor sobre una jurisdicción para ver su estado de cobertura.
    </p>
  );
}

function DetailContent({ jurisdiction }: { jurisdiction: Jurisdiction }) {
  const status = STATUS_LABELS[jurisdiction.status];
  return (
    <>
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-ink-muted">
        Jurisdicción
      </p>
      <h3 className="mt-1 font-serif text-xl leading-tight text-ink">
        {jurisdiction.name}
      </h3>

      <div className="mt-3 inline-flex items-center gap-2 text-sm">
        <StatusDot status={jurisdiction.status} />
        <span className="text-ink">{status.label}</span>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-ink-muted">
        {status.description}
      </p>

      {jurisdiction.designTitle && (
        <dl className="mt-4 space-y-2 border-t border-line pt-4 text-xs">
          <div>
            <dt className="uppercase tracking-wider text-ink-subtle">Diseño vigente</dt>
            <dd className="mt-0.5 font-medium text-ink">{jurisdiction.designTitle}</dd>
          </div>
          {jurisdiction.version && (
            <div>
              <dt className="uppercase tracking-wider text-ink-subtle">Versión</dt>
              <dd className="mt-0.5 font-medium text-ink">{jurisdiction.version}</dd>
            </div>
          )}
        </dl>
      )}
    </>
  );
}

// ─── Legend ──────────────────────────────────────────────────────────────────

function Legend({ counts }: { counts: Record<CoverageStatus, number> }) {
  return (
    <div>
      <p className="mb-3 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-ink-muted">
        Referencias
      </p>
      <ul className="space-y-2 text-sm">
        {(['covered', 'progress', 'pending'] as const).map((s) => (
          <li key={s} className="flex items-center gap-3">
            <StatusSwatch status={s} />
            <span className="text-ink">{STATUS_LABELS[s].label}</span>
            <span className="ml-auto font-mono text-xs text-ink-muted">{counts[s]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompactLegend({ counts }: { counts: Record<CoverageStatus, number> }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-muted">
      {(['covered', 'progress', 'pending'] as const).map((s) => (
        <li key={s} className="inline-flex items-center gap-2">
          <StatusSwatch status={s} compact />
          <span>{STATUS_LABELS[s].label}</span>
          <span className="font-mono text-ink-subtle">{counts[s]}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── Atoms ───────────────────────────────────────────────────────────────────

const SWATCH_FILL: Record<CoverageStatus, string> = {
  covered: 'bg-accent border-accent',
  progress: 'bg-status-progress/25 border-status-progress/50',
  pending: 'bg-paper-tinted border-line',
};

function StatusSwatch({ status, compact = false }: { status: CoverageStatus; compact?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block border',
        compact ? 'h-2.5 w-2.5' : 'h-3 w-3',
        SWATCH_FILL[status],
      )}
    />
  );
}

const DOT_FILL: Record<CoverageStatus, string> = {
  covered: 'bg-accent',
  progress: 'bg-status-progress',
  pending: 'bg-status-pending',
};

function StatusDot({ status }: { status: CoverageStatus }) {
  return <span aria-hidden="true" className={cn('inline-block h-2 w-2 rounded-full', DOT_FILL[status])} />;
}
